(() => {
  'use strict';

  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  const qs = (sel, el = document) => el.querySelector(sel);
  const qsa = (sel, el = document) => Array.from(el.querySelectorAll(sel));

  const fetchJson = async (url, init) => {
    const res = await fetch(url, { credentials: 'same-origin', ...init });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  };

  const getCart = async () => {
    try {
      return await fetchJson(`${root}cart.js`);
    } catch {
      return await fetchJson(`${root}cart.json`);
    }
  };

  const recoForProduct = async (productId, intent, limit) => {
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(
      String(productId)
    )}&limit=${encodeURIComponent(String(limit))}&intent=${encodeURIComponent(String(intent))}`;
    return await fetchJson(url);
  };

  const formatMoney = (cents) => {
    try {
      const n = Number(cents) / 100;
      const currency = window.Shopify?.currency?.active || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
    } catch {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const isPurchasable = (p) => Array.isArray(p?.variants) && p.variants.some((v) => v?.available);

  const productMaxVariantPriceCents = (p) => {
    const vs = Array.isArray(p?.variants) ? p.variants : [];
    let max = 0;
    for (const v of vs) {
      const price = Number(v?.price);
      if (Number.isFinite(price) && price > max) max = price;
    }
    return max;
  };

  const uniqById = (products) => {
    const map = new Map();
    for (const p of products) {
      if (!p?.id) continue;
      map.set(String(p.id), p);
    }
    return Array.from(map.values());
  };

  const setState = (wrap, { loading, text } = {}) => {
    const state = qs('[data-cdxs-state]', wrap);
    const stateText = qs('[data-cdxs-state-text]', wrap);

    if (loading) wrap.classList.add('is-loading');
    else wrap.classList.remove('is-loading');

    if (stateText && typeof text === 'string') stateText.textContent = text;
    if (state) state.style.display = loading ? 'inline-flex' : 'none';
  };

  const updateCartCount = (count) => {
    const n = Number(count);
    if (!Number.isFinite(n)) return;

    const selectors = [
      '[data-cart-count]',
      '[data-header-cart-count]',
      'cart-count',
      '.cart-count',
      '.cart-count-bubble__text',
      '.header-cart-count',
      '.mini-cart-count',
    ];

    const els = new Set();
    selectors.forEach((s) => qsa(s).forEach((el) => els.add(el)));

    els.forEach((el) => {
      if (el.matches('[data-wishlist-count]')) return;
      el.textContent = String(n);
      if (n <= 0) {
        el.setAttribute('hidden', '');
        el.classList.add('is-hidden');
      } else {
        el.removeAttribute('hidden');
        el.classList.remove('is-hidden');
      }
    });
  };

  /** Layout pinned on #mini-cart — survives cart-drawer innerHTML swaps */
  const getDrawerPinnedLayout = () => {
    const drawer = document.querySelector('#mini-cart');
    const layout = drawer?.getAttribute('data-cross-sell-layout');
    return layout === 'list' || layout === 'grid' ? layout : null;
  };

  const resolveLayout = (wrap) => {
    const pinned = getDrawerPinnedLayout();
    if (pinned) return pinned;

    if (!wrap) return 'grid';

    if (wrap.classList.contains('cdxs--list')) return 'list';
    if (wrap.classList.contains('cdxs--grid')) return 'grid';

    const attr = wrap.getAttribute('data-layout');
    if (attr === 'list' || attr === 'grid') return attr;

    return 'grid';
  };

  const applyLayout = (wrap) => {
    if (!wrap) return;
    const layout = resolveLayout(wrap);
    wrap.classList.remove('cdxs--list', 'cdxs--grid');
    wrap.classList.add(`cdxs--${layout}`);
    wrap.dataset.layout = layout;
    wrap.setAttribute('data-layout', layout);
  };

  const pickComplementary = async ({ cartItems, intent, limit }) => {
    const inCartProductIds = new Set(cartItems.map((it) => String(it.product_id)).filter(Boolean));

    const isBundleLine = (it) => {
      const tags =
        Array.isArray(it?.product_tags) ? it.product_tags :
        Array.isArray(it?.tags) ? it.tags :
        typeof it?.product_tags === 'string' ? it.product_tags.split(',') :
        typeof it?.tags === 'string' ? it.tags.split(',') :
        [];

      const tagsLower = tags.map((t) => String(t).trim().toLowerCase());
      if (tagsLower.includes('bundle')) return true;

      const title = String(it?.product_title || it?.title || '').toLowerCase();
      const url = String(it?.url || '').toLowerCase();
      const handle = String(it?.handle || '').toLowerCase();
      const productType = String(it?.product_type || '').toLowerCase();

      return (
        productType.includes('bundle') ||
        title.includes('bundle') ||
        url.includes('bundle') ||
        handle.includes('bundle')
      );
    };

    const seeds = cartItems
      .filter((it) => it?.product_id && !isBundleLine(it))
      .map((it) => {
        const qty = Number(it.quantity || 1);
        const final = Number(it.final_price ?? it.line_price ?? 0);
        const unit = qty > 0 ? Math.round(final / qty) : final;
        return { product_id: String(it.product_id), unit };
      })
      .filter((x) => x.product_id && Number.isFinite(x.unit))
      .sort((a, b) => b.unit - a.unit);

    if (!seeds.length) return [];

    for (const item of seeds) {
      try {
        const data = await recoForProduct(item.product_id, intent, Math.max(limit, 10));
        const products = Array.isArray(data?.products) ? data.products : [];

        const filtered = products
          .filter((p) => p?.id && !inCartProductIds.has(String(p.id)))
          .filter(isPurchasable);

        if (filtered.length) {
          return uniqById(filtered)
            .sort((a, b) => productMaxVariantPriceCents(b) - productMaxVariantPriceCents(a))
            .slice(0, limit);
        }
      } catch {
        /* try next seed */
      }
    }

    return [];
  };

  const buildCard = (product, wrap) => {
    const img = product?.featured_image || product?.images?.[0] || '';
    const priceCents = productMaxVariantPriceCents(product);

    const card = document.createElement('div');
    card.className = 'cdxs__card';
    card.innerHTML = `
      <div class="cdxs__img">${img ? `<img src="${img}" alt="">` : ''}</div>
      <div>
        <p class="cdxs__name"></p>
        <div class="cdxs__price"></div>
        <div class="cdxs__row">
          <select class="cdxs__select" aria-label="Select variant">
            <option value="">Select variant</option>
          </select>
          <button class="cdxs__btn" type="button" disabled>Add</button>
        </div>
        <div class="cdxs__note" hidden></div>
      </div>
    `;

    qs('.cdxs__name', card).textContent = product?.title || '';
    qs('.cdxs__price', card).textContent = formatMoney(priceCents);

    const select = qs('.cdxs__select', card);
    const btn = qs('.cdxs__btn', card);
    const note = qs('.cdxs__note', card);

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const availableVariants = variants.filter((v) => v?.id && v.available);

    for (const v of variants) {
      if (!v?.id) continue;
      const opt = document.createElement('option');
      opt.value = String(v.id);
      opt.textContent = `${v.title || 'Default'}${v.available ? '' : ' (Sold out)'}`;
      opt.disabled = !v.available;
      select.appendChild(opt);
    }

    if (availableVariants.length === 1) {
      select.value = String(availableVariants[0].id);
      select.hidden = true;
      btn.disabled = false;
    }

    select.addEventListener('change', () => {
      btn.disabled = !select.value;
      note.hidden = true;
      note.textContent = '';
    });

    btn.addEventListener('click', async () => {
      const variantId = select.value;
      if (!variantId) return;

      btn.disabled = true;
      const oldText = btn.textContent;
      btn.textContent = 'Adding…';
      note.hidden = true;

      try {
        const sectionsKey = wrap?.dataset?.sectionsKey || 'mini-cart';

        const addResponse = await fetchJson(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
            sections: [sectionsKey],
            sections_url: window.location.pathname,
          }),
        });

        const cart = await getCart();
        updateCartCount(cart?.item_count);

        if (addResponse?.sections) {
          cart.sections = addResponse.sections;
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
        );
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart,
              openMiniCart: false,
              replacementDelay: 50,
            },
          })
        );

        runAllRefreshes(true);
      } catch {
        btn.disabled = false;
        btn.textContent = oldText;
        note.hidden = false;
        note.textContent = 'Add failed. Please try again.';
      }
    });

    return card;
  };

  const cartKeyFromCart = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items
      .map((it) => `${it.product_id}:${it.quantity}`)
      .sort()
      .join('|');
  };

  const inflightRenders = new WeakMap();

  const renderWrap = async (wrap, force = false) => {
    if (inflightRenders.has(wrap)) {
      return inflightRenders.get(wrap);
    }

    const run = (async () => {
    applyLayout(wrap);

    const limit = Number(wrap.dataset.limit || 8);
    const intent = wrap.dataset.intent || 'complementary';

    const track = qs('[data-cdxs-track]', wrap);
    if (!track) return;

    let cart;
    try {
      cart = await getCart();
      updateCartCount(cart?.item_count);
    } catch {
      setState(wrap, { loading: true, text: 'Failed to load cart.' });
      return;
    }

    const key = cartKeyFromCart(cart);
    const alreadyRendered = track.querySelector('.cdxs__card') != null;

    if (!force && wrap.dataset.cdxsCartKey === key && alreadyRendered) {
      applyLayout(wrap);
      return;
    }

    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    if (!cartItems.length) {
      setState(wrap, { loading: true, text: 'Cart is empty.' });
      return;
    }

    setState(wrap, { loading: true, text: 'Loading…' });

    let products = await pickComplementary({ cartItems, intent, limit });

    if (!products.length) {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      products = await pickComplementary({ cartItems, intent, limit });
    }

    if (!products.length) {
      setState(wrap, { loading: true, text: 'No recommendations yet.' });
      return;
    }

    const frag = document.createDocumentFragment();
    for (const p of products) frag.appendChild(buildCard(p, wrap));

    track.innerHTML = '';
    track.appendChild(frag);

    wrap.dataset.cdxsCartKey = key;
    applyLayout(wrap);
    setState(wrap, { loading: false });
    })();

    inflightRenders.set(wrap, run);
    try {
      await run;
    } finally {
      inflightRenders.delete(wrap);
    }
  };

  let refreshBurstTimer = null;
  const REFRESH_DELAYS = [0, 300, 900];

  const scheduleRefresh = (delay = 300, force = false) => {
    window.setTimeout(() => {
      qsa('[data-cdxs]').forEach((wrap) => renderWrap(wrap, force));
    }, delay);
  };

  const runAllRefreshes = (force = false) => {
    if (refreshBurstTimer) window.clearTimeout(refreshBurstTimer);
    refreshBurstTimer = window.setTimeout(() => {
      refreshBurstTimer = null;
      REFRESH_DELAYS.forEach((delay) => scheduleRefresh(delay, force));
    }, 80);
  };

  window.CDXS_REFRESH = scheduleRefresh;
  window.CDXS_RUN_REFRESHES = runAllRefreshes;

  const boot = () => runAllRefreshes(true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  ['drawer:open', 'cart-drawer:open', 'cart:updated', 'cart:refresh'].forEach((evt) => {
    document.addEventListener(evt, () => runAllRefreshes(true), { passive: true });
  });

  document.addEventListener(
    'click',
    (e) => {
      const el = e.target?.closest?.('a,button');
      if (!el) return;

      const href = el.getAttribute('href') || '';
      const isCartChange = href.includes('/cart/change') || href.includes('/cart/update');
      const isRemove = el.matches('.line-item__remove-button') || href.includes('remove');

      if (isCartChange || isRemove) {
        runAllRefreshes(true);
      }
    },
    { passive: true }
  );

  const observeDrawerContent = () => {
    const miniCart = document.querySelector('#mini-cart');
    if (!miniCart || miniCart.__cdxsContentObserved) return;

    const content = miniCart.querySelector('.drawer__content');
    if (!content) return;

    miniCart.__cdxsContentObserved = true;

    const mo = new MutationObserver(() => {
      runAllRefreshes(true);
    });

    // subtree:false — 避免更新 .cdxs__track 时再次触发，造成循环请求
    mo.observe(content, { childList: true, subtree: false });
  };

  observeDrawerContent();

  let pollCount = 0;
  const poll = window.setInterval(() => {
    pollCount += 1;
    observeDrawerContent();
    if (qsa('[data-cdxs]').length) runAllRefreshes(true);
    if (pollCount >= 24) window.clearInterval(poll);
  }, 250);
})();
